import { useState, useRef, useEffect } from "react";
import { C, getFontFamily } from "../constants/colors";
import { LANG } from "../constants/lang";
import { SamajhIcon, LangToggle } from "./ui";

export const SPLASH_IMAGES = ["/images/splash-streetlight.png","/images/splash-chai.png"];

// Tagline phrases that swap in place — alternates between the two until splash unmounts.
const TAGLINE_PHRASES = { en: ["after class", "during exam"], hi: ["after class", "during exam"] };
const TAGLINE_FADE_MS = 250;
const TAGLINE_HOLD_MS = 2200;

export function Splash({ onStart, lang, setLang }) {
  const [phase, setPhase] = useState("hidden");
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const mouseRef = useRef({ x:-1000, y:-1000 });
  const ff = getFontFamily(lang);
  const t = LANG[lang];

  // Cycling tagline word: visible for ~2.2s, fade out, swap, fade in.
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [phraseVisible, setPhraseVisible] = useState(true);
  useEffect(() => {
    const phrases = TAGLINE_PHRASES[lang] || TAGLINE_PHRASES.en;
    if (phrases.length < 2) return undefined;
    const cycle = setInterval(() => {
      setPhraseVisible(false);
      setTimeout(() => {
        setPhraseIdx(i => (i + 1) % phrases.length);
        setPhraseVisible(true);
      }, TAGLINE_FADE_MS);
    }, TAGLINE_HOLD_MS + TAGLINE_FADE_MS);
    return () => clearInterval(cycle);
  }, [lang]);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("logo"), 300);
    const t2 = setTimeout(() => setPhase("tagline"), 1200);
    const t3 = setTimeout(() => setPhase("btn"), 2400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);

    const SYMBOLS = ["∑","π","F=ma","H₂O","x²","∫","△","∞","λ","√","α","β","θ","∂","E=mc²","PV=nRT","a²+b²","sin θ","v=u+at","½mv²","ΔH","pH","mol","ρ=m/v"];
    const symbols = Array.from({length:22},(_,i)=>{
      const x = 60+Math.random()*(window.innerWidth-120);
      const y = 40+Math.random()*(window.innerHeight-80);
      return {text:SYMBOLS[i%SYMBOLS.length],homeX:x,homeY:y,x,y,vx:0,vy:0,
        opacity:0.07+Math.random()*0.1,size:12+Math.random()*14,
        baseOpacity:0.07+Math.random()*0.1,
        driftX:(Math.random()-0.5)*0.08,driftY:(Math.random()-0.5)*0.06};
    });
    const particles = Array.from({length:55},()=>({
      x:Math.random()*window.innerWidth,y:Math.random()*window.innerHeight,
      vx:(Math.random()-0.5)*0.15,vy:(Math.random()-0.5)*0.15,
      r:0.8+Math.random()*1.2,opacity:0.12+Math.random()*0.18,
    }));

    const draw = () => {
      ctx.clearRect(0,0,canvas.width,canvas.height);
      const mx=mouseRef.current.x, my=mouseRef.current.y;
      for(let i=0;i<particles.length;i++){
        for(let j=i+1;j<particles.length;j++){
          const dx=particles[i].x-particles[j].x, dy=particles[i].y-particles[j].y;
          const d=Math.sqrt(dx*dx+dy*dy);
          if(d<70){ctx.beginPath();ctx.strokeStyle=`rgba(255,255,255,${0.08*(1-d/70)})`;ctx.lineWidth=0.4;ctx.moveTo(particles[i].x,particles[i].y);ctx.lineTo(particles[j].x,particles[j].y);ctx.stroke();}
        }
        const p=particles[i];
        p.x+=p.vx;p.y+=p.vy;
        if(p.x<0)p.x=canvas.width;if(p.x>canvas.width)p.x=0;
        if(p.y<0)p.y=canvas.height;if(p.y>canvas.height)p.y=0;
        ctx.beginPath();ctx.arc(p.x,p.y,p.r,0,Math.PI*2);ctx.fillStyle=`rgba(255,255,255,${p.opacity})`;ctx.fill();
      }
      symbols.forEach(s=>{
        const dx=s.x-mx,dy=s.y-my,dist=Math.sqrt(dx*dx+dy*dy);
        if(dist<110){const f=(110-dist)/110;s.vx+=(dx/dist)*f*1.8;s.vy+=(dy/dist)*f*1.8;s.opacity=Math.min(0.35,s.opacity+0.04);}
        else{s.vx+=(s.homeX-s.x)*0.018;s.vy+=(s.homeY-s.y)*0.018;s.opacity+=(s.baseOpacity-s.opacity)*0.04;}
        s.homeX+=s.driftX;s.homeY+=s.driftY;
        if(s.homeX<40||s.homeX>canvas.width-40)s.driftX*=-1;
        if(s.homeY<30||s.homeY>canvas.height-30)s.driftY*=-1;
        s.vx*=0.92;s.vy*=0.92;
        const spd=Math.sqrt(s.vx*s.vx+s.vy*s.vy);if(spd>4){s.vx=s.vx/spd*4;s.vy=s.vy/spd*4;}
        s.x+=s.vx;s.y+=s.vy;
        ctx.font=`${s.size}px 'Outfit', monospace`;ctx.fillStyle=`rgba(255,255,255,${s.opacity})`;ctx.fillText(s.text,s.x,s.y);
      });
      animRef.current=requestAnimationFrame(draw);
    };
    animRef.current=requestAnimationFrame(draw);

    const onMove=(e)=>{const rect=canvas.getBoundingClientRect();mouseRef.current={x:e.clientX-rect.left,y:e.clientY-rect.top};};
    const onLeave=()=>{mouseRef.current={x:-1000,y:-1000};};
    const onTouch=(e)=>{const rect=canvas.getBoundingClientRect();const t=e.touches[0];mouseRef.current={x:t.clientX-rect.left,y:t.clientY-rect.top};};
    canvas.addEventListener("mousemove",onMove);
    canvas.addEventListener("mouseleave",onLeave);
    canvas.addEventListener("touchmove",onTouch,{passive:true});
    return ()=>{cancelAnimationFrame(animRef.current);window.removeEventListener("resize",resize);};
  }, []);

  return (
    <div style={{fontFamily:ff,overflowX:"hidden"}}>
      <div style={{
        minHeight:"calc(100vh / 0.92)", zoom: 0.92,
        background:`
          radial-gradient(ellipse 100% 70% at 50% 25%, rgba(140,180,220,0.55) 0%, transparent 70%),
          radial-gradient(ellipse 80% 60% at 80% 75%, rgba(255,210,175,0.10) 0%, transparent 60%),
          linear-gradient(180deg, #5A8BB0 0%, #7AA5C8 25%, #A8C8DC 50%, #D0E0EC 75%, #EAF2F7 100%)
        `,
        display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
        position:"relative",overflow:"hidden",
      }}>
        {/* Canvas for animated particles + symbols */}
        <canvas ref={canvasRef} style={{position:"absolute",inset:0,width:"100%",height:"100%",zIndex:1}} />

        <div style={{position:"absolute",top:24,right:24,zIndex:10}}><LangToggle lang={lang} setLang={setLang}/></div>

        {/* Logo */}
        <div style={{
          position:"relative",zIndex:2,marginBottom:28,width:100,height:100,
          background:"#1E6B50",borderRadius:24,
          display:"flex",alignItems:"center",justifyContent:"center",
          boxShadow:"0 4px 20px rgba(30,107,80,0.35), inset 0 1px 0 rgba(255,255,255,0.15)",
          opacity:phase==="hidden"?0:1,transform:phase==="hidden"?"scale(0.6)":"scale(1)",
          transition:"opacity 0.8s cubic-bezier(0.34,1.56,0.64,1), transform 0.8s cubic-bezier(0.34,1.56,0.64,1)",
        }}>
          <span style={{fontFamily:"Georgia,'Times New Roman',serif",fontSize:54,fontWeight:700,color:"#9FE1CB",lineHeight:1,position:"relative"}}>
            S<span style={{position:"absolute",bottom:4,right:-3,width:10,height:10,background:"#9FE1CB",borderRadius:"50%",display:"block"}}/>
          </span>
        </div>

        {/* Samajh name */}
        <div style={{fontSize:40,fontWeight:700,color:"#1A3A4A",letterSpacing:"-0.03em",marginBottom:10,position:"relative",zIndex:2,opacity:phase==="hidden"?0:1,transition:"opacity 0.6s ease 0.2s"}}>Samajh</div>

        {/* Tagline — second half cycles between "after class" and "during exam" */}
        <div style={{fontSize:15,color:"#3A6A7A",fontWeight:400,textAlign:"center",maxWidth:280,lineHeight:1.5,position:"relative",zIndex:2,
          opacity:phase==="tagline"||phase==="btn"?1:0,transition:"opacity 0.6s ease"}}>
          Your study companion{" "}
          <span style={{
            display:"inline-block",
            opacity:phraseVisible?1:0,
            transform:phraseVisible?"translateY(0)":"translateY(-4px)",
            transition:`opacity ${TAGLINE_FADE_MS}ms ease, transform ${TAGLINE_FADE_MS}ms ease`,
            fontWeight:600,color:"#1E6B50",
          }}>
            {(TAGLINE_PHRASES[lang] || TAGLINE_PHRASES.en)[phraseIdx]}
          </span>
        </div>

        {/* CTA */}
        <div style={{marginTop:48,display:"flex",flexDirection:"column",gap:14,alignItems:"center",width:"100%",maxWidth:300,position:"relative",zIndex:2,opacity:phase==="btn"?1:0,transition:"opacity 0.5s ease"}}>
          <button onClick={onStart} style={{
            width:"100%",padding:"16px 0",fontSize:17,fontWeight:700,
            background:"#1E6B50",color:"white",border:"none",borderRadius:16,
            cursor:"pointer",fontFamily:ff,boxShadow:"0 4px 20px rgba(30,107,80,0.35)",transition:"transform .15s",
          }}
            onMouseOver={(e)=>e.currentTarget.style.transform="translateY(-2px)"}
            onMouseOut={(e)=>e.currentTarget.style.transform="translateY(0)"}
          >{lang==="hi"?"पढ़ाई शुरू करें →":"Start Learning →"}</button>
        </div>

        {/* Footer */}
        <div style={{position:"absolute",bottom:48,fontSize:12,color:"#5A8A9A",zIndex:2,opacity:phase==="btn"?1:0,transition:"opacity 0.5s ease 0.3s",display:"flex",alignItems:"center",gap:8}}>
          <svg width="18" height="12" viewBox="0 0 18 12" style={{borderRadius:1}}>
            <rect width="18" height="4" fill="#FF9933"/>
            <rect y="4" width="18" height="4" fill="#fff"/>
            <rect y="8" width="18" height="4" fill="#138808"/>
            <circle cx="9" cy="6" r="1.5" fill="none" stroke="#000080" strokeWidth="0.4"/>
          </svg>
          {lang==="hi"?"कक्षा 10 · CBSE":"Class 10 · CBSE · Hindi & English"}
        </div>
      </div>
    </div>
  );
}
